"use client";

import { GET_MENUS } from "@/graphql/queries/menus-query";
import { useCountryCode } from "@/hooks/useCountryCode";
import { useSuspenseQuery } from "@apollo/client";
import {
  Divider,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from "@repo/ui";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import React, { useMemo, useState } from "react";

import {
  AgileAndScrumIcon,
  DataAnalyticsIcon,
  DevOpsIcon,
  DigitalMarketingIcon,
  ItServiceManagementIcon,
  ProjectManagementIcon,
  QualityManagementIcon,
  SecurityIcon,
  UserCheck,
  WindowIcon,
} from "@spoctech/icons";

import { ChevronRight } from "lucide-react";
import { useParams } from "next/navigation";
import moment from "moment";

const CATEGORY_ICONS_MAPPING: Record<string, React.ReactNode> = {
  "agile-scrum-courses": (
    <AgileAndScrumIcon width={24} className="text-secondary-main" />
  ),
  "project-management-courses": (
    <ProjectManagementIcon width={24} className="text-secondary-main" />
  ),
  devops: <DevOpsIcon width={24} className="text-secondary-main" />,
  itsm: <ItServiceManagementIcon width={24} className="text-secondary-main" />,
  microsoft: <WindowIcon width={24} className="text-secondary-main" />,
  "quality-management": (
    <QualityManagementIcon width={24} className="text-secondary-main" />
  ),
  cybersecurity: <SecurityIcon width={24} className="text-secondary-main" />,
  "data-and-analytics": (
    <DataAnalyticsIcon width={24} className="text-secondary-main" />
  ),
  "leadership-professional-development": (
    <UserCheck width={24} className="text-secondary-main" />
  ),
  "digital-marketing": (
    <DigitalMarketingIcon width={24} className="text-secondary-main" />
  ),
};

const MegaMenu = () => {
  const { country, paramsHasCountry } = useCountryCode();
  const { category: activeCategory, course: activeCourse } = useParams<{
    category: string;
    course: string;
  }>();

  const { data } = useSuspenseQuery(GET_MENUS, {
    variables: {
      citySlug: "",
      countrySlug: "",
    },
  });

  const [selectedCategory, setSelectedCategory] = useState(() => {
    return data.menuQuery.menus.findIndex(
      (menu) => menu.slug === activeCategory
    ) !== -1
      ? data.menuQuery.menus.findIndex((menu) => menu.slug === activeCategory)
      : 0;
  });

  const accrediationLogos = useMemo(() => {
    return data.menuQuery.menus[selectedCategory]["courses"]
      .map((item) => ({
        name: item.accreditationName,
        logo: item.accreditationLogo,
        url: item.accreditationUrl,
      }))
      .filter((item) => item.logo !== null)
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.name === item.name)
      );
  }, [selectedCategory, data]);

  // Determine the correct chip color class based on priority
  const getCourseChipClass = (course: any) => {
    if (course.isPopular) return "text-primary-dark bg-primary-main/20";
    if (course.isSkillBasedCourses)
      return "text-success-main bg-success-light/25";
    if (course.isCertificationProgram)
      return "text-secondary-dark bg-secondary-light/25";
    return "";
  };

  const offer = useMemo(() => {
    if (data.menuQuery.offer) {
      return {
        ...data.menuQuery.offer,
        startDate: moment(data.menuQuery.offer.startDate).format("DD MMM YYYY"),
        endDate: moment(data.menuQuery.offer.endDate).format("DD MMM YYYY"),
      };
    }
    return null;
  }, [data.menuQuery.offer]);

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="bg-gray-100">
        View Courses
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div
          className="w-full divide-x divide-gray-300 grid "
          style={{ gridTemplateColumns: "auto 2fr 1fr" }}
        >
          <div className="pr-5">
            <h3 className="font-bold text-sm">Domains</h3>
            <Divider orientation="horizontal" />
            <ul>
              {data.menuQuery.menus.map((menu, index) => (
                <li key={menu.slug}>
                  <NavigationMenuLink
                    className={clsx("!flex gap-2 items-center group", {
                      "bg-gray-100 is-active": index === selectedCategory,
                    })}
                    onMouseEnter={() => setSelectedCategory(index)}
                    render={
                      <Link
                        href={
                          paramsHasCountry
                            ? `/${country}/topic/${menu.slug}`
                            : `/topic/${menu.slug}`
                        }
                      />
                    }
                  >
                    <span className="justify-start text-secondary-main">
                      {CATEGORY_ICONS_MAPPING[menu.slug] || (
                        <AgileAndScrumIcon
                          width={20}
                          className="text-secondary-main"
                        />
                      )}
                    </span>
                    <span>{menu.linkTitle}</span>
                    <span className="ml-auto opacity-0 group-[.is-active]:opacity-100">
                      <ChevronRight size={20} className="text-primary-light" />
                    </span>
                  </NavigationMenuLink>
                </li>
              ))}
            </ul>
          </div>
          <div className="px-5">
            <h3 className="font-bold text-sm">
              {data.menuQuery.menus[selectedCategory]["name"]}
            </h3>
            <Divider orientation="horizontal" />
            <ul>
              {data.menuQuery.menus[selectedCategory]["courses"].map(
                (course) => (
                  <li key={course.slug}>
                    <NavigationMenuLink
                      className={clsx("!py-2 text-xs font-semi-bold", {
                        "bg-gray-100 is-active": course.slug === activeCourse,
                      })}
                      render={
                        <Link
                          href={
                            paramsHasCountry
                              ? `/${country}/course/${course.slug}`
                              : `/course/${course.slug}`
                          }
                        />
                      }
                    >
                      <span className="flex gap-2 items-center">
                        {course.icon_url && (
                          <span className="block size-9 flex-grow-0">
                            <Image
                              src={course.icon_url}
                              width={24}
                              height={24}
                              alt={course.linkTitle}
                              className="w-full h-auto object-contain"
                            />
                          </span>
                        )}

                        <span className="flex-grow">
                          <span className="flex gap-2">
                            {course.linkTitle}
                            {/* chip */}
                            {(course.isPopular ||
                              course.isCertificationProgram ||
                              course.isSkillBasedCourses) && (
                              <span
                                className={clsx(
                                  "text-[10px] rounded-lg py-0.5 px-2 font-regular flex-shrink-0 flex",
                                  getCourseChipClass(course)
                                )}
                              >
                                {course.isPopular
                                  ? "Best Seller"
                                  : course.isSkillBasedCourses
                                    ? "Recommended"
                                    : course.isCertificationProgram
                                      ? "Trending"
                                      : ""}
                              </span>
                            )}
                          </span>
                          <span className="flex text-[10px] text-gray-500 font-regular">
                            {(() => {
                              type Item = {
                                key: string;
                                content: string;
                                className: string;
                              };

                              const items = [
                                course.accreditationName && {
                                  key: "accreditation",
                                  content: course.accreditationName,
                                  className: "pr-2",
                                },
                                course.serviceTrainingDays && {
                                  key: "trainingDays",
                                  content: course.serviceTrainingDays,
                                  className: "px-2",
                                },
                                course.modeOfTraining && {
                                  key: "mode",
                                  content: course.modeOfTraining,
                                  className: "pl-2",
                                },
                              ].filter((item): item is Item => Boolean(item));

                              return items.map((item, index) => (
                                <span
                                  key={item.key}
                                  className={`${
                                    items.length === 1 ? "" : item.className
                                  } ${
                                    items.length > 1 && index < items.length - 1
                                      ? "border-r border-gray-300"
                                      : ""
                                  }`}
                                >
                                  {item.content}
                                </span>
                              ));
                            })()}
                          </span>
                        </span>
                      </span>
                    </NavigationMenuLink>
                  </li>
                )
              )}
            </ul>
          </div>
          <div className="pl-5 flex flex-col justify-between ">
            {accrediationLogos.length > 0 && (
              <div>
                <h3 className="font-bold mb-4 text-sm text-zinc-900">
                  Accreditation Bodies
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {accrediationLogos.map((accrediation, index) => (
                    <div
                      key={`acc-${index}-${accrediation.name}`}
                      className=" overflow-hidden flex gap-3 items-center"
                    >
                      <Image
                        src={accrediation.logo}
                        alt={`acc-${accrediation.name}`}
                        width={100}
                        height={40}
                        // fill
                        // sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-contain object-left w-20 h-10"
                      />
                      {/* <span>{accrediation.name}</span> */}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {offer && (
              <div>
                <h3 className="font-bold mb-4 text-sm text-zinc-900">
                  Exciting Offers
                </h3>
                <p className="text-gray-600 text-xs">
                  {offer.startDate} - {offer.endDate}
                </p>
                <div className="bg-yellow-100 p-4 rounded-lg mt-3">
                  <p className="font-bold text-gray-700">{offer.name}</p>
                  <p className="text-gray-600 my-2">{offer.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

export default MegaMenu;
